/**
 * What an attachment is, and what to call it on screen.
 *
 * A card can hold three kinds: a screenshot (an image, shown inline), a file
 * (anything else we hold), and a link (a URL we do not hold — a Drive file, a
 * Doc, a Sheet).
 *
 * Pure, so the URL parsing and the size rules can be checked without a browser.
 */

export type AttachmentKind = "screenshot" | "file" | "link";

export interface Attachment {
  id: string;
  task_id: string;
  kind: AttachmentKind;
  storage_path: string | null;
  link_url: string | null;
  title: string | null;
  file_name: string | null;
  file_type: string | null;
  file_size: number | null;
  uploaded_by: string | null;
  created_at: string;
}

/** What kind of thing a URL points at, as far as we can tell from the URL. */
export type LinkSource =
  | "drive" | "docs" | "sheets" | "slides" | "forms"
  | "dropbox" | "notion" | "figma" | "shopify" | "web";

export interface LinkDescription {
  source: LinkSource;
  /** "Google Sheet", "Google Drive file", "Link" — what to put on the chip. */
  label: string;
  /** The Drive/Docs file id, when the URL carries one. */
  fileId?: string;
  host: string;
}

const GOOGLE_DOC = /^\/(document|spreadsheets|presentation|forms)\/d\/([A-Za-z0-9_-]+)/;
const DRIVE_FILE = /^\/file\/d\/([A-Za-z0-9_-]+)/;

const GOOGLE_LABEL: Record<string, { source: LinkSource; label: string }> = {
  document:     { source: "docs",   label: "Google Doc" },
  spreadsheets: { source: "sheets", label: "Google Sheet" },
  presentation: { source: "slides", label: "Google Slides" },
  forms:        { source: "forms",  label: "Google Form" },
};

/**
 * Recognise the common ones so a chip can say "Google Sheet" rather than
 * showing 68 characters of URL. Anything unrecognised is still a link — this
 * describes, it does not gatekeep.
 */
export function describeLink(url: string): LinkDescription {
  let u: URL;
  try { u = new URL(url); } catch { return { source: "web", label: "Link", host: "" }; }
  const host = u.hostname.replace(/^www\./, "");

  if (host === "docs.google.com") {
    const m = GOOGLE_DOC.exec(u.pathname);
    if (m) {
      const kind = GOOGLE_LABEL[m[1]];
      return { ...kind, fileId: m[2], host };
    }
    return { source: "drive", label: "Google Drive", host };
  }
  if (host === "drive.google.com") {
    const m = DRIVE_FILE.exec(u.pathname);
    if (m) return { source: "drive", label: "Google Drive file", fileId: m[1], host };
    if (u.pathname.startsWith("/drive/folders/")) {
      return { source: "drive", label: "Google Drive folder",
               fileId: u.pathname.split("/drive/folders/")[1]?.split("/")[0], host };
    }
    return { source: "drive", label: "Google Drive", host };
  }
  if (host.endsWith("dropbox.com")) return { source: "dropbox", label: "Dropbox", host };
  if (host.endsWith("notion.so") || host.endsWith("notion.site")) return { source: "notion", label: "Notion", host };
  if (host.endsWith("figma.com")) return { source: "figma", label: "Figma", host };
  if (host.endsWith("myshopify.com") || host === "admin.shopify.com") return { source: "shopify", label: "Shopify", host };
  return { source: "web", label: host || "Link", host };
}

/**
 * Is this a link we will accept?
 *
 * Only http(s). A `javascript:` URL in a field that renders as a clickable chip
 * is stored XSS with a friendly label on it. The database refuses it too; this
 * is so somebody finds out while they are typing.
 */
export function linkProblem(raw: string): string | null {
  const s = raw.trim();
  if (!s) return "Paste a link first.";
  let u: URL;
  try { u = new URL(s); } catch {
    return s.includes(".") && !s.includes(" ")
      ? "That needs the https:// on the front."
      : "That does not look like a web address.";
  }
  if (u.protocol !== "http:" && u.protocol !== "https:") {
    return `Only web links work here — ${u.protocol} links are not allowed.`;
  }
  return null;
}

/** What to show on the chip, given what we know. */
export function attachmentLabel(a: Attachment): string {
  if (a.title?.trim()) return a.title.trim();
  if (a.kind === "link" && a.link_url) {
    const d = describeLink(a.link_url);
    return d.label;
  }
  return a.file_name || "Attachment";
}

/**
 * The biggest file worth putting on a card.
 *
 * Generous for a document and a photograph, small enough that nobody uses a
 * task card as a video host. Someone with a 200MB file has a Drive link to
 * attach instead, which is the better answer anyway.
 */
export const MAX_FILE_BYTES = 25 * 1024 * 1024;

export function fileProblem(file: { name: string; size: number; type: string }): string | null {
  if (file.size === 0) return `${file.name} is empty.`;
  if (file.size > MAX_FILE_BYTES) {
    return `${file.name} is ${formatBytes(file.size)}. The limit is ${formatBytes(MAX_FILE_BYTES)} — `
      + "put it in Drive and attach the link instead.";
  }
  return null;
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

/** A storage path that cannot escape its task's folder or break the bucket. */
export function storagePathFor(taskId: string, fileName: string, now = Date.now()): string {
  const safe = (fileName || "file")
    // Anything that is not a letter, digit, dot or dash becomes an underscore,
    // which is what removes the separators a traversal needs.
    .replace(/[^\w.-]+/g, "_")
    // …and then no run of dots survives at all. `..` cannot traverse once the
    // slashes are gone, but leaving it there means the next person has to work
    // that out, and a name nobody has to reason about is worth more.
    .replace(/\.{2,}/g, ".")
    .replace(/^[._-]+/, "")
    // The end, not the start: that is where the extension is.
    .slice(-120) || "file";
  return `${taskId}/${now}-${safe}`;
}

export function kindForFile(type: string): AttachmentKind {
  return type.startsWith("image/") ? "screenshot" : "file";
}
